import type { AttendancePoint } from '@/lib/types';

export type LocationCaptureErrorCode = 'unsupported' | 'denied' | 'unavailable' | 'timeout' | 'unknown';

export class LocationCaptureError extends Error {
  readonly code: LocationCaptureErrorCode;

  constructor(message: string, code: LocationCaptureErrorCode) {
    super(message);
    this.name = 'LocationCaptureError';
    this.code = code;
  }
}

function mapGeolocationError(error: GeolocationPositionError): LocationCaptureErrorCode {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'denied';
    case error.POSITION_UNAVAILABLE:
      return 'unavailable';
    case error.TIMEOUT:
      return 'timeout';
    default:
      return 'unknown';
  }
}

function messageForCode(code: LocationCaptureErrorCode) {
  switch (code) {
    case 'unsupported':
      return 'Location is not supported in this browser. Use a device with GPS or switch to a supported browser.';
    case 'denied':
      return 'Location access is blocked. Enable location permission in your browser settings to clock in or out.';
    case 'unavailable':
      return 'Your current location could not be determined. Check that location services are turned on and try again.';
    case 'timeout':
      return 'Location request timed out. Move to an area with better signal and try again.';
    case 'unknown':
    default:
      return 'Could not capture your location. Please try again.';
  }
}

export function isLocationPermissionError(error: unknown) {
  return error instanceof LocationCaptureError && (error.code === 'denied' || error.code === 'unsupported');
}

export async function captureCurrentLocation(): Promise<AttendancePoint> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new LocationCaptureError(messageForCode('unsupported'), 'unsupported');
  }

  if (navigator.permissions?.query) {
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });

      if (status.state === 'denied') {
        throw new LocationCaptureError(messageForCode('denied'), 'denied');
      }
    } catch (error) {
      if (error instanceof LocationCaptureError) {
        throw error;
      }
    }
  }

  return new Promise<AttendancePoint>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy ?? null,
        });
      },
      (error) => {
        const code = mapGeolocationError(error);
        reject(new LocationCaptureError(messageForCode(code), code));
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  });
}
